import { useCallback, useMemo, useState } from "react";

import type { Note, UUID } from "@/core/types";

import { isNoteSelectable } from "../(list)/helpers/isNoteSelectable";

export function useNotesSelection(filteredNotes: Note[] | undefined) {
  const [selectedIds, setSelectedIds] = useState<Set<UUID>>(new Set());

  // Derives visible selection by intersecting with current filtered notes,
  // replacing the useEffect-based sync in the previous implementation
  const visibleSelectedIds = useMemo(() => {
    const visibleIds = new Set(filteredNotes?.map((n) => n.id));

    return new Set([...selectedIds].filter((id) => visibleIds.has(id)));
  }, [filteredNotes, selectedIds]);

  const selectableNotes = filterSelectableNotes(filteredNotes);

  const isAllSelected =
    selectableNotes?.length && visibleSelectedIds.size === selectableNotes?.length;

  const toggleNote = useCallback((id: UUID) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds(isAllSelected ? new Set() : new Set(selectableNotes?.map((n) => n.id)));
  }, [selectableNotes, isAllSelected]);

  return { isAllSelected, toggleAll, toggleNote, visibleSelectedIds };
}

function filterSelectableNotes(notes: Note[] | undefined) {
  return notes?.filter(isNoteSelectable);
}
